import { useTranslation } from 'react-i18next'

import Loading from '../../../../../loading/Loading'
import { usePriceRuleFormat } from '../priceRuleFormat'
import { RulePrices } from '../priceRuleParts'
import { ScheduleCourts, ScheduleDays, changedClass } from '../../../scheduleCard/scheduleParts'

import { PriceRule } from '../../../../../../types/booking/booking'

type PriceRuleVersionsProps = {
  /** Every version of the rule, newest first; null while loading. */
  history: PriceRule[] | null
  courtNames: Map<number | undefined, string>
}

/**
 * Previous versions of one price rule. Each entry shows its own values, with what it changed from the version before it
 * highlighted; changed prices carry an up or down arrow.
 */
export const PriceRuleVersions = ({ history, courtNames }: PriceRuleVersionsProps) => {
  const { t } = useTranslation('pricing')
  const format = usePriceRuleFormat()

  if (history === null) {
    return (
      <div className="schedule-card-versions">
        <Loading variant="inline" />
      </div>
    )
  }

  // Newest first: the first entry is the version the card already shows.
  const previous = history.slice(1)

  return (
    <div className="schedule-card-versions">
      <div className="schedule-card-versions-title tiny muted">{t('pricing.card.previous-versions')}</div>
      {previous.length === 0 && <div className="tiny muted">{t('pricing.card.no-previous-versions')}</div>}
      {previous.map((version, index) => {
        // The version this one replaced (the next one down the list); the oldest has nothing to compare with.
        const replaced = previous[index + 1]
        const timesChanged =
          replaced !== undefined &&
          (version.interval.startTime !== replaced.interval.startTime || version.interval.endTime !== replaced.interval.endTime)

        return (
          <div key={version.id} className="schedule-card-version">
            <div className="schedule-card-version-header">
              <div className="schedule-card-version-title">
                <span className="mono fw-semibold">{t('pricing.manager.version', { version: version.version })}</span>
                <span className="tiny muted">– {format.period(version)}</span>
                {replaced !== undefined && version.name !== replaced.name && (
                  <span className="has-changed schedule-card-version-mark">{version.name}</span>
                )}
              </div>
            </div>

            {/* Same columns as the card (when, where, how much), with courts and prices at the card's fixed widths. */}
            <div className="schedule-card-version-facts">
              <div className="schedule-card-version-fact">
                <span className={`mono fw-semibold schedule-card-version-mark${changedClass(timesChanged)}`}>
                  {format.startTimes(version)}
                </span>
                <ScheduleDays weekdays={version.weekdays} previous={replaced?.weekdays} />
              </div>
              <div className="schedule-card-version-fact is-courts">
                <ScheduleCourts courtIds={version.courtIds} courtNames={courtNames} previous={replaced?.courtIds} />
              </div>
              <div className="schedule-card-version-fact is-prices">
                <RulePrices rule={version} previous={replaced} compact />
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
